import {
	Autocomplete,
	Box,
	Checkbox,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	TextField,
	Typography
} from "@mui/material";
import moment, { Moment } from "moment";
import { DatePicker, renderTimeViewClock, TimePicker } from "@mui/x-date-pickers-pro";
import { Controller, RegisterOptions, useForm } from "react-hook-form";
import React, { FC, useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";

import {
	IAuthorizedListRowDetails,
	IAuthorizedVehicleDetails,
	ICreateVehicleOwnerVehicleDetails,
	ICreateVehicleOwnerVehiclesRequestData,
	IEditVehicleOwnerVehicleDetails,
	IEditVehicleOwnerVehiclesRequestData,
	ISocietyConfigDetails
} from "../../../../types";
import {
	useAddVehicleOwnerMutation,
	useDeleteVehicleOwnerVehicleMutation,
	useEditVehicleOwnerMutation
} from "../../../../redux/reducers/dataManagement.reducer";
import {
	useGetDevicesListQuery,
	useGetExcelValidationsQuery,
	useGetOwnerTypesQuery,
	useGetSocietyConfigQuery,
	useGetVehicleTypesQuery
} from "../../../../redux/reducers/settings.reducer";
import NumberInput from "../../../../components/NumberInput";
import { convertToTitleCase } from "../../../../utils/commonUtils";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import SecureLoginAccessButton from "../../../../components/SecureLoginAccessButton";
import { openSecureLoginDialog } from "../../../../redux/reducers/secureLoginDialog.reducer";

interface IAuthorizedMemberDetailsDialogProps {
	open: boolean;
	memberDetails: IAuthorizedListRowDetails | null;
	onClose: () => void;
}

type IAddedVehicleDetails = Omit<IAuthorizedVehicleDetails, "id">;

interface IAddedVehicleErrors {
	[index: number]: string;
}

interface IFlatDetails {
	key: string;
	building: string;
	flatNumber: string;
}

interface IFormFields {
	name: string;
	contact_number: string;
	flat_no: string;
	owner_type: number;
	validity_start_date?: Moment | null;
	validity_start_time?: Moment | null;
	validity_end_date?: Moment | null;
	validity_end_time?: Moment | null;
	devices: number[];
}

const AuthorizedMemberDetailsDialog: FC<IAuthorizedMemberDetailsDialogProps> = (props) => {
	const { open, memberDetails, onClose } = props;

	const dispatch = useAppDispatch();

	const authState = useAppSelector((state) => state.auth);

	const [existingVehiclesList, setExistingVehiclesList] = useState<IAuthorizedVehicleDetails[]>([]);
	const [addedVehiclesList, setAddedVehiclesList] = useState<IAddedVehicleDetails[]>([]);

	const [deletingVehicleIds, setDeletingVehicleIds] = useState<number[]>([]);
	const [deletingAddedVehicleIds, setDeletingAddedVehicleIds] = useState<number[]>([]);

	const [addedVehiclesError, setAddedVehiclesError] = useState<IAddedVehicleErrors>({});
	const [existingVehiclesError, setExistingVehiclesError] = useState<IAddedVehicleErrors>({});

	const [customFlatSelectionEnabled, setCustomFlatSelectionEnabled] = useState<boolean>(false);
	const [apiError, setApiError] = useState<string>("");

	const ReadOnlyInput = useMemo<boolean>(
		() =>
			(authState.user.secure_login_enabled_society || authState.user.secure_login_enabled_user) &&
			authState.user.login_type !== "secure",
		[authState.user]
	);

	// APIS
	const { data: getOwnerTypesResponse } = useGetOwnerTypesQuery();
	const ownerTypesList = useMemo(() => getOwnerTypesResponse?.results ?? [], [getOwnerTypesResponse]);

	const { data: excelValidations } = useGetExcelValidationsQuery();

	const { data: getSocietyConfigResponse } = useGetSocietyConfigQuery();
	const societyDetails: ISocietyConfigDetails | null =
		getSocietyConfigResponse && getSocietyConfigResponse.results.length > 0
			? getSocietyConfigResponse.results[0]
			: null;

	const { data: getDevicesListResponse } = useGetDevicesListQuery({});
	const devicesList = getDevicesListResponse ? getDevicesListResponse.results : [];

	// GET VEHICLE TYPES
	const { data: getVehicleTypesResponse } = useGetVehicleTypesQuery();

	const vehicleTypesList = useMemo(() => getVehicleTypesResponse ?? [], [getVehicleTypesResponse]);

	const [
		addVehicleOwner,
		{
			isSuccess: addVehicleOwnerSuccess,
			isLoading: addVehicleOwnerLoading,
			error: addVehicleOwnerError,
			reset: resetAddVehicleOwnerUpdatedState
		}
	] = useAddVehicleOwnerMutation();

	const [
		editVehicleOwner,
		{
			isSuccess: editVehicleOwnerSuccess,
			isLoading: editVehicleOwnerLoading,
			error: editVehicleOwnerError,
			reset: resetEditVehicleOwnerState
		}
	] = useEditVehicleOwnerMutation();

	const [deleteVehicleOwnerVehicles] = useDeleteVehicleOwnerVehicleMutation();

	const FlatsList = useMemo(() => {
		const flatsList: IFlatDetails[] = [];

		if (societyDetails) {
			for (const buildingDetails of societyDetails.building_config) {
				for (const buildingEntry of buildingDetails.entries) {
					if (buildingDetails.name === "Common Area") {
						flatsList.push({
							key: buildingEntry,
							building: buildingDetails.name,
							flatNumber: buildingEntry
						});
					} else {
						flatsList.push({
							key: `${buildingDetails.name}${buildingDetails.delimiter ?? "-"}${buildingEntry}`,
							building: buildingDetails.name,
							flatNumber: buildingEntry
						});
					}
				}
			}
		}

		return flatsList;
	}, [societyDetails]);

	const {
		control,
		reset,
		watch,
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<IFormFields>();

	const selectedOwnerType = watch("owner_type");

	const showValidityInputs = useMemo<boolean>(() => {
		if (selectedOwnerType) {
			const selectedOwnerTypeDetails = ownerTypesList.find(
				(ownerTypeItem) => ownerTypeItem.id === Number(selectedOwnerType)
			);

			if (selectedOwnerTypeDetails && selectedOwnerTypeDetails.has_expiry) {
				return true;
			}
		}

		return false;
	}, [ownerTypesList, selectedOwnerType]);

	function handleAddNewVehicle() {
		setAddedVehiclesList((currentVehiclesList) => {
			const updatedVehiclesList = [...currentVehiclesList];

			updatedVehiclesList.push({
				vehicle_number: "",
				vehicle_type: 0
			});

			return updatedVehiclesList;
		});
	}

	function handleChangeExistingVehicleType(index: number, updatedValue: number) {
		setExistingVehiclesList((currentVehiclesList) => {
			const updatedVehiclesList = [...currentVehiclesList];

			updatedVehiclesList[index] = {
				...updatedVehiclesList[index],
				vehicle_type: updatedValue
			};

			return updatedVehiclesList;
		});
	}

	function handleChangeExistingVehicleNumber(index: number, updatedValue: string) {
		setExistingVehiclesList((currentVehiclesList) => {
			const updatedVehiclesList = [...currentVehiclesList];

			updatedVehiclesList[index] = {
				...updatedVehiclesList[index],
				vehicle_number: updatedValue
			};

			return updatedVehiclesList;
		});
	}

	function handleRevertDeletedExistingVehicle(vehicleId: number) {
		setDeletingVehicleIds((currentVehicleIds) => {
			const updatedVehicleIds = currentVehicleIds.filter((item) => item !== vehicleId);
			return updatedVehicleIds;
		});
	}

	function handleDeleteExistingVehicle(vehicleId: number) {
		setDeletingVehicleIds((currentVehicleIds) => {
			const updatedVehicleIds = [...currentVehicleIds];
			updatedVehicleIds.push(vehicleId);
			return updatedVehicleIds;
		});
	}

	function handleChangeAddedVehicleType(index: number, updatedValue: number) {
		setAddedVehiclesList((currentVehiclesList) => {
			const updatedVehiclesList = [...currentVehiclesList];

			updatedVehiclesList[index] = {
				...updatedVehiclesList[index],
				vehicle_type: updatedValue
			};

			return updatedVehiclesList;
		});
	}

	function handleChangeAddedVehicleNumber(index: number, updatedValue: string) {
		setAddedVehiclesList((currentVehiclesList) => {
			const updatedVehiclesList = [...currentVehiclesList];

			updatedVehiclesList[index] = {
				...updatedVehiclesList[index],
				vehicle_number: updatedValue
			};

			return updatedVehiclesList;
		});
	}

	function handleRevertDeletedAddedVehicle(index: number) {
		setDeletingAddedVehicleIds((currentVehicleIds) => {
			const updatedVehicleIds = [...currentVehicleIds];
			updatedVehicleIds.splice(index, 1);
			return updatedVehicleIds;
		});
	}

	function handleDeleteAddedVehicle(index: number) {
		setDeletingAddedVehicleIds((currentVehicleIds) => {
			const updatedVehicleIds = [...currentVehicleIds];
			updatedVehicleIds.push(index);
			return updatedVehicleIds;
		});
	}

	async function handleSubmitForm(data: IFormFields) {
		const selectedOwnerTypeDetails = ownerTypesList.find((item) => item.id === Number(data.owner_type));

		if (memberDetails) {
			const formData: IEditVehicleOwnerVehiclesRequestData = {
				id: memberDetails.id,
				name: data.name ? convertToTitleCase(data.name) : null,
				phone_number: data.contact_number ? data.contact_number : null,
				owner_type: Number(data.owner_type),
				society: societyDetails ? societyDetails.id : 1,
				vehicles: [],
				flat_number: data.flat_no ? data.flat_no : null,
				is_custom_flat_number: customFlatSelectionEnabled,
				device: data.devices
			};

			// Number(data.owner_type) === memberOwnerTypeId ||

			// if (selectedOwnerTypeDetails && selectedOwnerTypeDetails.has_expiry) {
			// 	formData.flat_number = data.flat_no;
			// 	formData.is_custom_flat_number = customFlatSelectionEnabled;
			// }

			let startDate = null;
			let endDate = null;

			if (selectedOwnerTypeDetails && selectedOwnerTypeDetails.has_expiry) {
				const startTime = moment(data.validity_start_time);
				const endTime = moment(data.validity_end_time);

				startDate = moment(data.validity_start_date)
					.set("hours", startTime.get("hours"))
					.set("minutes", startTime.get("minutes"));

				endDate = moment(data.validity_end_date)
					.set("hours", endTime.get("hours"))
					.set("minutes", endTime.get("minutes"));

				formData.validity_start_date = startDate.toISOString();
				formData.validity_end_date = endDate.toISOString();
			}

			if (deletingVehicleIds.length > 0) {
				const response = await deleteVehicleOwnerVehicles(deletingVehicleIds);

				if ("error" in response && "data" in response.error) {
					setApiError(response.error.data ? String(response.error.data) : "Could not delete vehicles");
					return;
				}
			}

			for (const vehicleItem of existingVehiclesList) {
				if (deletingVehicleIds.includes(vehicleItem.id)) continue;

				const vehicleDetails: IEditVehicleOwnerVehicleDetails = {
					id: vehicleItem.id,
					number_plate: vehicleItem.vehicle_number.toUpperCase(),
					vehicle_type: vehicleItem.vehicle_type,
					auth_type: "whitelist",
					society: societyDetails?.id ?? 1,
					owner: memberDetails.id
				};

				if (startDate && endDate) {
					vehicleDetails.validity_start_date = startDate.toISOString();
					vehicleDetails.validity_end_date = endDate.toISOString();
				}

				formData.vehicles.push(vehicleDetails);
			}

			const updatedVehicleErrors: IAddedVehicleErrors = {};

			for (const [index, vehicleItem] of Object.entries(addedVehiclesList)) {
				if (deletingAddedVehicleIds.includes(Number(index))) continue;

				if (
					excelValidations &&
					excelValidations.number_plate.regex &&
					!excelValidations.number_plate.regex.test(vehicleItem.vehicle_number.toUpperCase())
				) {
					updatedVehicleErrors[Number(index)] = "Invalid vehicle number";
					continue;
				}

				formData.vehicles.push({
					number_plate: vehicleItem.vehicle_number.toUpperCase(),
					vehicle_type: vehicleItem.vehicle_type,
					auth_type: "whitelist",
					society: societyDetails ? societyDetails.id : 1,
					owner: memberDetails.id
				});
			}

			setAddedVehiclesError(updatedVehicleErrors);

			if (Object.keys(updatedVehicleErrors).length <= 0) {
				editVehicleOwner(formData);
			}
		} else {
			const formData: ICreateVehicleOwnerVehiclesRequestData = {
				name: data.name ? data.name : null,
				phone_number: data.contact_number ? data.contact_number : null,
				owner_type: Number(data.owner_type),
				society: societyDetails ? societyDetails.id : 1,
				vehicles: [],
				flat_number: data.flat_no ? data.flat_no : null,
				is_custom_flat_number: customFlatSelectionEnabled,
				device: data.devices
			};

			// formData.owner_type === memberOwnerTypeId ||

			// if (selectedOwnerTypeDetails && selectedOwnerTypeDetails.has_expiry) {
			// 	formData.flat_number = data.flat_no;
			// 	formData.is_custom_flat_number = customFlatSelectionEnabled;
			// }

			let startDate = null;
			let endDate = null;

			if (selectedOwnerTypeDetails && selectedOwnerTypeDetails.has_expiry) {
				const startTime = moment(data.validity_start_time);
				const endTime = moment(data.validity_end_time);

				startDate = moment(data.validity_start_date)
					.set("hours", startTime.get("hours"))
					.set("minutes", startTime.get("minutes"));

				endDate = moment(data.validity_end_date)
					.set("hours", endTime.get("hours"))
					.set("minutes", endTime.get("minutes"));

				formData.validity_start_date = startDate.toISOString();
				formData.validity_end_date = endDate.toISOString();
			}

			const updatedVehicleErrors: IAddedVehicleErrors = {};

			for (const [index, vehicleItem] of Object.entries(addedVehiclesList)) {
				if (deletingAddedVehicleIds.includes(Number(index))) continue;

				if (
					excelValidations &&
					excelValidations.number_plate.regex &&
					!excelValidations.number_plate.regex.test(vehicleItem.vehicle_number.toUpperCase())
				) {
					updatedVehicleErrors[Number(index)] = "Invalid vehicle number";
					continue;
				}

				const vehicleDetails: ICreateVehicleOwnerVehicleDetails = {
					number_plate: vehicleItem.vehicle_number.toUpperCase(),
					vehicle_type: vehicleItem.vehicle_type,
					auth_type: "whitelist",
					society: societyDetails?.id ?? 1
				};

				if (startDate && endDate) {
					vehicleDetails.validity_start_date = startDate.toISOString();
					vehicleDetails.validity_end_date = endDate.toISOString();
				}

				formData.vehicles.push(vehicleDetails);
			}

			setAddedVehiclesError(updatedVehicleErrors);

			if (Object.keys(updatedVehicleErrors).length <= 0) {
				addVehicleOwner(formData);
			}
		}
	}

	function handleChangeCustomFlatSelectionEnabled(isChecked: boolean) {
		setCustomFlatSelectionEnabled(isChecked);
	}

	function getValidationError(
		errorKey: keyof IFormFields,
		type: "required" | "pattern" | "max_length",
		limit?: number
	): string {
		const requiredErrorTemplate = "{key} is required";
		const patternErrorTemplate = "Enter a valid {key}";
		const maxLengthErrorTemplate = `{key} should not be longer than ${limit ?? 0} characters`;

		let key = "";

		if (errorKey === "name") key = "name";
		if (errorKey === "contact_number") key = "contact number";
		if (errorKey === "flat_no") key = "unit number";

		if (type === "required") return requiredErrorTemplate.replace("{key}", convertToTitleCase(key));
		if (type === "pattern") return patternErrorTemplate.replace("{key}", key);
		if (type === "max_length") return maxLengthErrorTemplate.replace("{key}", convertToTitleCase(key));

		return "";
	}

	function getValidations<T extends keyof IFormFields>(key: T): RegisterOptions<IFormFields, T> {
		if (!excelValidations) return {};

		let validationOptions = null;

		if (key === "name") validationOptions = excelValidations.name;
		else if (key === "contact_number") validationOptions = excelValidations.phone_number;
		else if (key === "flat_no") validationOptions = excelValidations.flat_number;

		if (!validationOptions) return {};

		const validations: RegisterOptions = {};

		if (!validationOptions.allow_null) {
			validations.required = getValidationError(key, "required");
		}

		if (typeof validationOptions.char_size === "number") {
			validations.maxLength = {
				value: validationOptions.char_size,
				message: getValidationError(key, "max_length", validationOptions.char_size)
			};
		}

		if (validationOptions.regex) {
			validations.pattern = {
				value: validationOptions.regex,
				message: getValidationError(key, "pattern")
			};
		}

		return validations;
	}

	function handleOpenSecureLoginDialog() {
		dispatch(openSecureLoginDialog());
	}

	useEffect(() => {
		if (open) {
			if (memberDetails) {
				reset({
					name: memberDetails.name,
					flat_no: memberDetails.flat_no,
					contact_number: memberDetails.contact_number,
					owner_type: memberDetails.owner_type_id,
					validity_start_date: memberDetails.validity_start_date ? moment(memberDetails.validity_start_date) : null,
					validity_end_date: memberDetails.validity_end_date ? moment(memberDetails.validity_end_date) : null,
					devices: memberDetails.devices ?? [],
					validity_start_time: memberDetails.validity_start_date ? moment(memberDetails.validity_start_date) : null,
					validity_end_time: memberDetails.validity_end_date ? moment(memberDetails.validity_end_date) : null
				});

				setExistingVehiclesList(memberDetails.vehicles);
				setCustomFlatSelectionEnabled(memberDetails.is_custom_flat_number);
			} else {
				reset({
					name: "",
					flat_no: "",
					contact_number: "",
					owner_type: 0,
					validity_start_date: null,
					validity_end_date: null,
					devices: devicesList.map((item) => item.id),
					validity_start_time: null,
					validity_end_time: null
				});

				setExistingVehiclesList([]);
				setCustomFlatSelectionEnabled(false);
			}
		} else {
			setAddedVehiclesList([]);
			setDeletingVehicleIds([]);
			setDeletingAddedVehicleIds([]);
			setAddedVehiclesError({});
			setExistingVehiclesError({});
			// setShowValidityInputs(false);
			resetAddVehicleOwnerUpdatedState();
			resetEditVehicleOwnerState();
			setApiError("");

			// reset({
			// 	name: "",
			// 	flat_no: "",
			// 	contact_number: "",
			// 	owner_type: 0,
			// 	validity_start_date: null,
			// 	validity_end_date: null
			// });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, memberDetails, devicesList]);

	useEffect(() => {
		if (addVehicleOwnerSuccess) {
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addVehicleOwnerSuccess]);

	useEffect(() => {
		if (editVehicleOwnerSuccess) {
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editVehicleOwnerSuccess]);

	// useEffect(() => {
	// 	if (selectedOwnerType) {
	// 		const selectedOwnerTypeDetails = ownerTypesList.find(
	// 			(ownerTypeItem) => ownerTypeItem.id === Number(selectedOwnerType)
	// 		);

	// 		if (selectedOwnerTypeDetails && selectedOwnerTypeDetails.has_expiry) {
	// 			setShowValidityInputs(true);
	// 		} else {
	// 			setShowValidityInputs(false);
	// 		}
	// 	}
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [selectedOwnerType]);

	useEffect(() => {
		if (FlatsList.length > 0) {
			setCustomFlatSelectionEnabled(false);
		} else {
			setCustomFlatSelectionEnabled(true);
		}
	}, [FlatsList.length]);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle
				textAlign="center"
				sx={{
					background: "#30ae3d",
					color: "white",
					position: "relative",
					padding: { xs: "0.75rem 1rem 0.5rem 1rem", sm: "1rem 1.25rem 0.75rem 1.25rem" },
					borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: 600,
							fontSize: { xs: "1.125rem", sm: "1.25rem" }
						}}
					>
						{memberDetails ? "Edit Authorized Member" : "Add Authorized Member"}
					</Typography>
				</Box>

				<Box sx={{ position: "absolute", top: { xs: 8, sm: 12 }, right: { xs: 8, sm: 12 } }}>
					<IconButton
						onClick={onClose}
						sx={{
							color: "white",
							background: "rgba(255, 255, 255, 0.1)",
							backdropFilter: "blur(8px)",
							borderRadius: "50%",
							padding: { xs: "4px", sm: "6px" },
							"&:hover": {
								background: "rgba(255, 255, 255, 0.2)",
								transform: "scale(1.1)"
							},
							transition: "all 0.2s ease-in-out"
						}}
					>
						<CloseIcon sx={{ fontSize: { xs: "20px", sm: "24px" } }} />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent
				sx={{
					padding: { xs: "1rem !important", sm: "1.25rem !important" },
					background: "#ffffff",
					overflowY: "auto"
				}}
			>
				<Grid
					container
					spacing={{ xs: 2, sm: 3 }}
					component="form"
					noValidate
					onSubmit={handleSubmit(handleSubmitForm)}
				>
					<Grid item xs={12} md={6}>
						<InputLabel
							htmlFor="name-input"
							required
							error={!!errors.name}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: { xs: "0.8125rem", sm: "0.875rem" }
							}}
						>
							Name
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="outlined"
							color="primary"
							size="small"
							id="name-input"
							placeholder="Enter the name of person"
							autoComplete="off"
							error={!!errors.name}
							helperText={errors.name && errors.name.message ? errors.name.message.toString() : ""}
							InputProps={{ readOnly: ReadOnlyInput }}
							inputProps={{ style: { textTransform: "capitalize" } }}
							{...register("name", getValidations("name"))}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required={excelValidations && !excelValidations.phone_number.allow_null}
							htmlFor="contact-number-input"
							error={!!errors.contact_number}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: { xs: "0.8125rem", sm: "0.875rem" }
							}}
						>
							Contact Number
						</InputLabel>

						<NumberInput
							required={excelValidations && !excelValidations.phone_number.allow_null}
							fullWidth
							isContactNumber
							positiveOnly
							disableSigned
							disableDecimal
							variant="outlined"
							color="primary"
							size="small"
							id="contact-number-input"
							placeholder="Enter the contact number"
							autoComplete="off"
							error={!!errors.contact_number}
							helperText={
								errors.contact_number && errors.contact_number.message ? errors.contact_number.message.toString() : ""
							}
							InputProps={{ readOnly: ReadOnlyInput }}
							{...register("contact_number", getValidations("contact_number"))}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="owner-type-input"
							error={!!errors.owner_type}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: { xs: "0.8125rem", sm: "0.875rem" }
							}}
						>
							Owner Type
						</InputLabel>

						<Controller
							name="owner_type"
							control={control}
							rules={{ min: { value: 1, message: "Owner Type is required" } }}
							render={({ field: { value, onChange } }) => (
								<FormControl
									variant="outlined"
									color="primary"
									size="small"
									fullWidth
									required
									error={!!errors.owner_type}
									id="owner-type-input"
								>
									<Select required fullWidth value={value} onChange={onChange} readOnly={ReadOnlyInput}>
										{ownerTypesList
											? ownerTypesList.map((ownerTypeItem) => (
													<MenuItem value={ownerTypeItem.id} key={ownerTypeItem.id}>
														{ownerTypeItem.type_of}
													</MenuItem>
											  ))
											: null}
									</Select>

									{errors.owner_type && errors.owner_type.message ? (
										<FormHelperText>{errors.owner_type.message.toString()}</FormHelperText>
									) : null}
								</FormControl>
							)}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="area-of-visit-input"
							error={!!errors.flat_no}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: { xs: "0.8125rem", sm: "0.875rem" }
							}}
						>
							Area of Visit
						</InputLabel>

						{customFlatSelectionEnabled ? (
							<TextField
								required
								fullWidth
								variant="outlined"
								color="primary"
								size="small"
								id="area-of-visit-input"
								placeholder="Enter the area of visit"
								autoComplete="off"
								error={!!errors.flat_no}
								helperText={errors.flat_no && errors.flat_no.message ? errors.flat_no.message.toString() : ""}
								{...register("flat_no", getValidations("flat_no"))}
							/>
						) : (
							<Controller
								name="flat_no"
								control={control}
								defaultValue=""
								rules={getValidations("flat_no")}
								render={({ field: { value, onChange } }) => (
									<Autocomplete
										fullWidth
										size="small"
										id="area-of-visit-input"
										options={FlatsList}
										getOptionLabel={(option) => option.key}
										getOptionKey={(option) => option.key}
										groupBy={(option) => option.building}
										value={FlatsList.find((item) => item.key === value) ?? null}
										onChange={(_, updatedValue) => onChange(updatedValue?.key ?? null)}
										noOptionsText="No Units"
										readOnly={ReadOnlyInput}
										renderInput={(params) => (
											<TextField
												{...params}
												required
												fullWidth
												variant="outlined"
												color="primary"
												label="Area of Visit"
												placeholder="Select the area of visit"
												autoComplete="off"
												error={!!errors.flat_no}
												helperText={errors.flat_no && errors.flat_no.message ? errors.flat_no.message.toString() : ""}
											/>
										)}
									/>
								)}
							/>
						)}

						{!ReadOnlyInput ? (
							<FormControlLabel
								control={
									<Checkbox
										checked={customFlatSelectionEnabled}
										onChange={(_, checked) => handleChangeCustomFlatSelectionEnabled(checked)}
									/>
								}
								label="Custom Area of Visit"
								sx={{
									"& .MuiFormControlLabel-label": {
										fontSize: { xs: "0.8125rem", sm: "0.875rem" }
									}
								}}
							/>
						) : null}
					</Grid>

					{showValidityInputs ? (
						<>
							<Grid item xs={12} md={6}>
								<InputLabel
									required
									htmlFor="validity-start-date-input"
									error={!!errors.validity_start_date}
									sx={{
										fontWeight: 600,
										color: "#3D3D3D",
										marginBottom: "0.5rem",
										fontSize: { xs: "0.8125rem", sm: "0.875rem" }
									}}
								>
									Validity Start Date
								</InputLabel>

								<Controller
									name="validity_start_date"
									control={control}
									rules={{
										required: showValidityInputs ? "Start date is required" : false
									}}
									render={({ field: { value, onChange } }) => (
										<DatePicker
											format="DD/MM/YYYY"
											value={value}
											onChange={onChange}
											readOnly={ReadOnlyInput}
											slotProps={{
												textField: {
													required: true,
													fullWidth: true,
													color: "primary",
													variant: "outlined",
													size: "small",
													id: "validity-start-date-input",
													placeholder: "Select validity start date",
													error: !!errors.validity_start_date,
													helperText:
														errors.validity_start_date && errors.validity_start_date.message
															? errors.validity_start_date.message.toString()
															: ""
												}
											}}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12} md={6}>
								<InputLabel
									required
									htmlFor="validity-start-time-input"
									error={!!errors.validity_start_time}
									sx={{
										fontWeight: 600,
										color: "#3D3D3D",
										marginBottom: "0.5rem",
										fontSize: { xs: "0.8125rem", sm: "0.875rem" }
									}}
								>
									Validity Start Time
								</InputLabel>

								<Controller
									control={control}
									name="validity_start_time"
									rules={{ required: showValidityInputs ? "Start time is required" : false }}
									render={({ field: { value, onChange } }) => (
										<TimePicker
											format="hh:mm A"
											value={value}
											onChange={onChange}
											readOnly={ReadOnlyInput}
											closeOnSelect={false}
											slotProps={{
												textField: {
													required: true,
													fullWidth: true,
													color: "primary",
													variant: "outlined",
													size: "small",
													id: "validity-start-time-input",
													placeholder: "Select validity start time",
													error: !!errors.validity_start_time,
													helperText:
														errors.validity_start_time && errors.validity_start_time.message
															? errors.validity_start_time.message.toString()
															: ""
												}
											}}
											viewRenderers={{ hours: renderTimeViewClock, minutes: renderTimeViewClock }}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12} md={6}>
								<InputLabel
									required
									htmlFor="validity-end-date-input"
									error={!!errors.validity_end_date}
									sx={{
										fontWeight: 600,
										color: "#3D3D3D",
										marginBottom: "0.5rem",
										fontSize: { xs: "0.8125rem", sm: "0.875rem" }
									}}
								>
									Validity End Date
								</InputLabel>

								<Controller
									name="validity_end_date"
									control={control}
									rules={{ required: showValidityInputs ? "End date is required" : false }}
									render={({ field: { value, onChange } }) => (
										<DatePicker
											format="DD/MM/YYYY"
											value={value}
											onChange={onChange}
											readOnly={ReadOnlyInput}
											slotProps={{
												textField: {
													required: true,
													fullWidth: true,
													color: "primary",
													variant: "outlined",
													size: "small",
													id: "validity-end-date-input",
													placeholder: "Select validity end date",
													error: !!errors.validity_end_date,
													helperText:
														errors.validity_end_date && errors.validity_end_date.message
															? errors.validity_end_date.message.toString()
															: ""
												}
											}}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12} md={6}>
								<InputLabel
									required
									htmlFor="validity-end-time-input"
									error={!!errors.validity_end_time}
									sx={{
										fontWeight: 600,
										color: "#3D3D3D",
										marginBottom: "0.5rem",
										fontSize: { xs: "0.8125rem", sm: "0.875rem" }
									}}
								>
									Validity End Time
								</InputLabel>

								<Controller
									control={control}
									name="validity_end_time"
									rules={{ required: showValidityInputs ? "End time is required" : false }}
									render={({ field: { value, onChange } }) => (
										<TimePicker
											format="hh:mm A"
											value={value}
											onChange={onChange}
											readOnly={ReadOnlyInput}
											closeOnSelect={false}
											slotProps={{
												textField: {
													required: true,
													fullWidth: true,
													color: "primary",
													variant: "outlined",
													size: "small",
													id: "validity-end-time-input",
													placeholder: "Select validity end time",
													error: !!errors.validity_end_time,
													helperText:
														errors.validity_end_time && errors.validity_end_time.message
															? errors.validity_end_time.message.toString()
															: ""
												}
											}}
											viewRenderers={{ hours: renderTimeViewClock, minutes: renderTimeViewClock }}
										/>
									)}
								/>
							</Grid>
						</>
					) : null}

					<Grid item xs={12} md={6}>
						<InputLabel
							error={!!errors.devices}
							htmlFor="devices-input"
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: { xs: "0.8125rem", sm: "0.875rem" }
							}}
						>
							Devices
						</InputLabel>

						<Controller
							control={control}
							name="devices"
							rules={{
								validate: (value) => {
									if (value.length <= 0) {
										return "At least one device should be selected";
									}

									return true;
								}
							}}
							render={({ field: { value, onChange } }) => (
								<>
									<Autocomplete
										fullWidth
										multiple
										options={devicesList}
										getOptionLabel={(option) => option.device_name}
										getOptionKey={(option) => option.id}
										id="devices-input"
										disableCloseOnSelect
										limitTags={1}
										value={devicesList.filter((item) => value.includes(item.id))}
										onChange={(_, updatedValue) => onChange(updatedValue.map((item) => item.id))}
										readOnly={ReadOnlyInput}
										renderOption={(props, option, { selected }) => (
											<li {...props} style={{ gap: "0.5rem" }}>
												{selected ? (
													<CheckBoxIcon color="primary" sx={{ marginY: "0.25rem" }} />
												) : (
													<CheckBoxOutlineBlankIcon color="primary" sx={{ marginY: "0.25rem" }} />
												)}

												{option.device_name}
											</li>
										)}
										renderInput={(params) => (
											<TextField
												{...params}
												fullWidth
												hiddenLabel
												variant="outlined"
												size="small"
												placeholder="Select the devices"
												sx={{ height: "100%" }}
												error={!!errors.devices}
												helperText={errors.devices && errors.devices.message ? errors.devices.message.toString() : ""}
												InputProps={{
													...params.InputProps,
													hiddenLabel: true
												}}
											/>
										)}
										renderTags={() => ""}
										PaperComponent={(props) => {
											const { children, ...restProps } = props;
											const AllDevicesSelected =
												value.length === devicesList.length ? "all" : value.length === 0 ? "none" : "partial";

											return (
												<Paper {...restProps}>
													<Box
														onMouseDown={(event) => event.preventDefault()}
														sx={{ paddingX: "1.25rem", paddingY: "0.25rem", width: "100%" }}
													>
														<FormControlLabel
															label="Select All"
															sx={{ width: "100%" }}
															control={
																<Checkbox
																	checked={AllDevicesSelected === "all"}
																	indeterminate={AllDevicesSelected === "partial"}
																/>
															}
															onClick={(event) => {
																event.preventDefault();
																if (AllDevicesSelected === "none" || AllDevicesSelected === "partial") {
																	onChange(devicesList.map((item) => item.id));
																} else if (AllDevicesSelected === "all") {
																	onChange([]);
																}
															}}
														/>
													</Box>

													<Divider />
													{children}
												</Paper>
											);
										}}
									/>

									<Typography variant="body2" sx={{ marginTop: "0.25rem", marginLeft: "0.25rem" }}>
										{value.length === 0
											? "No devices selected"
											: value.length === devicesList.length
											? "All devices selected"
											: `${value.length} device${value.length !== 1 ? "s" : ""} selected`}
									</Typography>
								</>
							)}
						/>
					</Grid>

					<Grid item xs={12}>
						<Box
							className="vehicles-list-wrapper"
							sx={{
								background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
								borderRadius: "0.5rem",
								padding: { xs: "0.5rem 0.75rem 0.75rem", sm: "0.5rem 1rem 1rem" },
								border: "1px solid rgba(0, 0, 0, 0.08)",
								boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)"
							}}
						>
							<Typography
								variant="h6"
								fontWeight={700}
								sx={{
									color: "#2c3e50",
									display: "flex",
									alignItems: "center",
									gap: 1,
									fontSize: { xs: "1rem", sm: "1.25rem" }
								}}
							>
								<DirectionsCarFilledIcon sx={{ color: "#4caf50", fontSize: { xs: "20px", sm: "24px" } }} />
								Known list of Vehicles
							</Typography>

							{existingVehiclesList.map((vehicleItem, index) => (
								<Box
									sx={{
										border: "1px solid #4caf50",
										borderRadius: "0.5rem",
										display: "flex",
										flexDirection: { xs: "column", sm: "row" },
										alignItems: { xs: "stretch", sm: "center" },
										gap: { xs: 1, sm: 2 },
										padding: { xs: "0.75rem", sm: "1rem" },
										background: "white",
										boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
										transition: "all 0.3s ease",
										"&:hover": {
											boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
										}
									}}
									key={index}
								>
									<Box sx={{ flex: 1 }}>
										<InputLabel
											required
											sx={{
												fontWeight: 600,
												color: "#3D3D3D",
												marginBottom: "0.5rem",
												fontSize: { xs: "0.8125rem", sm: "0.875rem" }
											}}
										>
											Vehicle Type
										</InputLabel>

										<FormControl
											required
											fullWidth
											variant="outlined"
											color="primary"
											size="small"
											disabled={deletingVehicleIds.includes(vehicleItem.id)}
										>
											<Select
												required
												fullWidth
												readOnly
												value={vehicleItem.vehicle_type}
												onChange={(event) => handleChangeExistingVehicleType(index, Number(event.target.value))}
											>
												{vehicleTypesList.map((vehicleTypeItem) => (
													<MenuItem value={vehicleTypeItem.id} key={vehicleTypeItem.id}>
														{vehicleTypeItem.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Box>

									<Box sx={{ flex: 1 }}>
										<InputLabel
											required
											error={!!existingVehiclesError[vehicleItem.id]}
											sx={{
												fontWeight: 600,
												color: "#3D3D3D",
												marginBottom: "0.5rem",
												fontSize: { xs: "0.8125rem", sm: "0.875rem" }
											}}
										>
											Vehicle Number
										</InputLabel>

										<TextField
											required
											fullWidth
											disabled={deletingVehicleIds.includes(vehicleItem.id)}
											variant="outlined"
											color="primary"
											size="small"
											autoComplete="off"
											placeholder="Enter the vehicle number"
											value={vehicleItem.vehicle_number}
											onChange={(event) => handleChangeExistingVehicleNumber(index, event.target.value)}
											InputProps={{ readOnly: true }}
											error={!!existingVehiclesError[vehicleItem.id]}
											helperText={existingVehiclesError ? existingVehiclesError[vehicleItem.id] : ""}
											classes={{ root: "vehicle-number-input-root" }}
										/>
									</Box>

									{!ReadOnlyInput ? (
										<Box
											sx={{
												display: "flex",
												justifyContent: { xs: "flex-end", sm: "center" },
												alignItems: "center"
											}}
										>
											{deletingVehicleIds.includes(vehicleItem.id) ? (
												<IconButton
													onClick={() => handleRevertDeletedExistingVehicle(vehicleItem.id)}
													sx={{
														color: "#4caf50",
														"&:hover": {
															backgroundColor: "rgba(76, 175, 80, 0.1)"
														}
													}}
												>
													<CloseIcon />
												</IconButton>
											) : (
												<IconButton
													onClick={() => handleDeleteExistingVehicle(vehicleItem.id)}
													sx={{
														color: "#f44336",
														"&:hover": {
															backgroundColor: "rgba(244, 67, 54, 0.1)"
														}
													}}
												>
													<DeleteIcon />
												</IconButton>
											)}
										</Box>
									) : null}
								</Box>
							))}

							{addedVehiclesList.map((vehicleItem, index) => (
								<Box
									sx={{
										border: "1px solid #4caf50",
										borderRadius: "0.5rem",
										display: "flex",
										flexDirection: { xs: "column", sm: "row" },
										alignItems: { xs: "stretch", sm: "center" },
										gap: { xs: 1, sm: 2 },
										padding: { xs: "0.75rem", sm: "1rem" },
										background: "white",
										boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
										transition: "all 0.3s ease",
										"&:hover": {
											boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
										}
									}}
									key={index}
								>
									<Box sx={{ flex: 1 }}>
										<InputLabel
											required
											sx={{
												fontWeight: 600,
												color: "#3D3D3D",
												marginBottom: "0.5rem",
												fontSize: { xs: "0.8125rem", sm: "0.875rem" }
											}}
										>
											Vehicle Type
										</InputLabel>

										<FormControl
											required
											fullWidth
											variant="outlined"
											color="primary"
											size="small"
											disabled={deletingAddedVehicleIds.includes(index)}
										>
											<Select
												required
												fullWidth
												value={vehicleItem.vehicle_type}
												onChange={(event) => handleChangeAddedVehicleType(index, Number(event.target.value))}
											>
												{vehicleTypesList.map((ownerTypeItem) => (
													<MenuItem value={ownerTypeItem.id} key={ownerTypeItem.id}>
														{ownerTypeItem.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Box>

									<Box sx={{ flex: 1 }}>
										<InputLabel
											required
											error={!!addedVehiclesError[index]}
											sx={{
												fontWeight: 600,
												color: "#3D3D3D",
												marginBottom: "0.5rem",
												fontSize: { xs: "0.8125rem", sm: "0.875rem" }
											}}
										>
											Vehicle Number
										</InputLabel>

										<TextField
											required
											fullWidth
											disabled={deletingAddedVehicleIds.includes(index)}
											variant="outlined"
											color="primary"
											size="small"
											autoComplete="off"
											placeholder="Enter the vehicle number"
											value={vehicleItem.vehicle_number}
											onChange={(event) => handleChangeAddedVehicleNumber(index, event.target.value)}
											error={!!addedVehiclesError[index]}
											helperText={addedVehiclesError ? addedVehiclesError[index] : ""}
											classes={{ root: "vehicle-number-input-root" }}
										/>
									</Box>

									{!ReadOnlyInput ? (
										<Box
											sx={{
												display: "flex",
												justifyContent: { xs: "flex-end", sm: "center" },
												alignItems: "center"
											}}
										>
											{deletingAddedVehicleIds.includes(index) ? (
												<IconButton
													onClick={() => handleRevertDeletedAddedVehicle(index)}
													sx={{
														color: "#4caf50",
														"&:hover": {
															backgroundColor: "rgba(76, 175, 80, 0.1)"
														}
													}}
												>
													<CloseIcon />
												</IconButton>
											) : (
												<IconButton
													onClick={() => handleDeleteAddedVehicle(index)}
													sx={{
														color: "#f44336",
														"&:hover": {
															backgroundColor: "rgba(244, 67, 54, 0.1)"
														}
													}}
												>
													<DeleteIcon />
												</IconButton>
											)}
										</Box>
									) : null}
								</Box>
							))}

							{!ReadOnlyInput ? (
								<Box
									sx={{
										border: "2px dashed #4caf50",
										borderRadius: "0.5rem",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: 1,
										padding: { xs: "1rem", sm: "1.25rem" },
										cursor: "pointer",
										transition: "all 0.3s ease",
										background: "rgba(76, 175, 80, 0.02)",
										"&:hover": {
											background: "rgba(76, 175, 80, 0.08)",
											borderColor: "#66bb6a",
											transform: "translateY(-1px)"
										}
									}}
									onClick={handleAddNewVehicle}
								>
									<AddCircleIcon sx={{ color: "#4caf50", fontSize: { xs: "20px", sm: "24px" } }} />
									<Typography
										fontWeight={600}
										sx={{
											color: "#4caf50",
											fontSize: { xs: "0.875rem", sm: "1rem" }
										}}
									>
										Add new Vehicle
									</Typography>
								</Box>
							) : null}
						</Box>
					</Grid>

					{apiError ? (
						<Grid item xs={12}>
							<Typography
								variant="body2"
								color="var(--color-error-main)"
								textAlign="center"
								sx={{
									fontSize: { xs: "0.8125rem", sm: "0.875rem" }
								}}
							>
								{apiError}
							</Typography>
						</Grid>
					) : null}

					{addVehicleOwnerError && "data" in addVehicleOwnerError ? (
						<Grid item xs={12}>
							<Typography
								variant="body2"
								color="var(--color-error-main)"
								textAlign="center"
								sx={{
									fontSize: { xs: "0.8125rem", sm: "0.875rem" }
								}}
							>
								{addVehicleOwnerError.data ? String(addVehicleOwnerError.data) : "Something went wrong"}
							</Typography>
						</Grid>
					) : null}

					{editVehicleOwnerError && "data" in editVehicleOwnerError ? (
						<Grid item xs={12}>
							<Typography
								variant="body2"
								color="var(--color-error-main)"
								textAlign="center"
								sx={{
									fontSize: { xs: "0.8125rem", sm: "0.875rem" }
								}}
							>
								{editVehicleOwnerError.data ? String(editVehicleOwnerError.data) : "Something went wrong"}
							</Typography>
						</Grid>
					) : null}

					{!ReadOnlyInput ? (
						<Grid item xs={12}>
							<Box
								sx={{
									display: "flex",
									flexDirection: { xs: "column", sm: "row" },
									alignItems: "center",
									justifyContent: "center",
									gap: { xs: "0.75rem", sm: "1rem" }
								}}
							>
								<SecureLoginAccessButton
									tooltipAction={handleOpenSecureLoginDialog}
									variant="contained"
									color="primary"
									type="submit"
									loading={memberDetails ? editVehicleOwnerLoading : addVehicleOwnerLoading}
									sx={{
										width: { xs: "100%", sm: "auto" },
										fontSize: { xs: "0.875rem", sm: "1rem" },
										padding: { xs: "8px 16px", sm: "8px 16px" }
									}}
								>
									Save
								</SecureLoginAccessButton>
							</Box>
						</Grid>
					) : null}
				</Grid>
			</DialogContent>
		</Dialog>
	);
};

export default AuthorizedMemberDetailsDialog;
